import {
	Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, SimpleChange,
	ViewChildren, ViewChild, AfterViewInit, ElementRef, QueryList, ChangeDetectionStrategy,
	ChangeDetectorRef
} from '@angular/core';
import {
	SnmpSettings, WindowsSettings, TelnetSettings, SshSettings,
	VimSettings, PrtgSettings, HttpSettings
} from 'DiscoveryEngine.TS/Protocols';
import { ProtocolSettings, ProtocolSettingsTypes, SnmpVersion, ProtocolSettingsUtils } from 'DiscoveryEngine.TS/Protocols';
import { NetViewTreeNodeDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { Changeable, Crypto } from 'common';
import { NetFacadeService } from 'netview';
import { LicensingService } from 'identity';
import { Accordion } from 'primeng/accordion';


enum SettingsPanel { None, Snmp, SnmpV3, Windows, Telnet, Ssh, Http, Vim, Prtg };


enum NavType { None, SelectSettings, AddSettings, DeleteSettings, ClosePanel, OpenPanel };


@Component({
	selector: 'credentials',
	templateUrl: './credentials.component.html',
	styleUrls: ['./credentials.component.scss']
	//, changeDetection: ChangeDetectionStrategy.OnPush
})
export class CredentialsComponent implements OnChanges, AfterViewInit, Changeable {

	@Input() public target: NetViewTreeNodeDto;
	public get hasTarget(): boolean {
		return !!this.target;
	}

	// Because the view uses *ngIf on these components,
	// they will be null most of the time, thus making
	// the use of ViewChildren instead of ViewChild necessary
	@ViewChildren('snmp,snmpv3,windows,telnet,ssh,http,vim,prtg')
	public children: QueryList<Changeable>;

	public selectedSnmpSettings: SnmpSettings;
	public selectedSnmpV3Settings: SnmpSettings;
	public selectedWindowsSettings: WindowsSettings;
	public selectedTelnetSettings: TelnetSettings;
	public selectedSshSettings: SshSettings;
	public selectedVimSettings: VimSettings;
	public selectedPrtgSettings: PrtgSettings;
	public selectedHttpSettings: HttpSettings;

	public allowShowChars: boolean;

	public allSnmpSettings: SnmpSettings[];
	public allSnmpV3Settings: SnmpSettings[];
	public allWindowsSettings: WindowsSettings[];
	public allTelnetSettings: TelnetSettings[];
	public allSshSettings: SshSettings[];
	public allVimSettings: VimSettings[];
	public allPrtgSettings: PrtgSettings[];
	public allHttpSettings: HttpSettings[];

	constructor(private netFacadeService: NetFacadeService,
				private licensingService: LicensingService,
				private changeDetector: ChangeDetectorRef) {
		this.target = null;
		this.resetSelectedSettings();
		this.allowShowChars = false;
		this.rebuildSettingsLists();
	}

	public get noSelection(): boolean {
		return (!this.selectedSnmpSettings &&
			!this.selectedSnmpV3Settings &&
			!this.selectedWindowsSettings &&
			!this.selectedTelnetSettings &&
			!this.selectedSshSettings &&
			!this.selectedVimSettings &&
			!this.selectedPrtgSettings &&
			!this.selectedHttpSettings);
	}

	private onAppliedChange(): void {
		if (this.hasTarget) {
			this.target.HasChanges = true;
			this.allowShowChars = false;
		}
	}

	public ngAfterViewInit(): void {
		this.snmpHeader.nativeElement.parentNode.parentNode.parentNode.addEventListener("click", (e) => {
			this.onHeaderClick(0);
		});
		this.snmpV3Header.nativeElement.parentNode.parentNode.parentNode.addEventListener("click", (e) => {
			this.onHeaderClick(1);
		});
		this.windowsHeader.nativeElement.parentNode.parentNode.parentNode.addEventListener("click", (e) => {
			this.onHeaderClick(2);
		});
		this.telnetHeader.nativeElement.parentNode.parentNode.parentNode.addEventListener("click", (e) => {
			this.onHeaderClick(3);
		});
		this.sshHeader.nativeElement.parentNode.parentNode.parentNode.addEventListener("click", (e) => {
			this.onHeaderClick(4);
		});
		this.httpHeader.nativeElement.parentNode.parentNode.parentNode.addEventListener("click", (e) => {
			this.onHeaderClick(5);
		});
		this.vimHeader.nativeElement.parentNode.parentNode.parentNode.addEventListener("click", (e) => {
			this.onHeaderClick(6);
		});
		this.prtgHeader.nativeElement.parentNode.parentNode.parentNode.addEventListener("click", (e) => {
			this.onHeaderClick(7);
		});
	}

	public get isLicenseValid(): boolean {
		//return this.licensingService.isLicenseValid;
		return true;
	}

	private onHeaderClick(panelIndex: number): void {
		let event = {
			index: panelIndex
		};

		if (panelIndex == this.activePanelIndex) {
			this.onClosePanel(event);
		}
		else {
			this.onOpenPanel(event);
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.resetSelectedSettings();
		this.rebuildSettingsLists();
	}

	private rebuildSettingsLists(): void {
		this.allSnmpSettings = [];
		this.allSnmpV3Settings = [];
		this.allWindowsSettings = [];
		this.allTelnetSettings = [];
		this.allSshSettings = [];
		this.allVimSettings = [];
		this.allPrtgSettings = [];
		this.allHttpSettings = [];

		if (this.hasTarget) {
			this.target.ProtocolSettings.ForEach((ps: ProtocolSettings) => {
				switch (ps.SettingsType) {
					case ProtocolSettingsTypes.Snmp:
						{
							let snmp = <SnmpSettings>ps;
							if (snmp.Version === SnmpVersion.Ver3) {
								this.allSnmpV3Settings = [...this.allSnmpV3Settings, snmp];
							}
							else {
								this.allSnmpSettings = [...this.allSnmpSettings, snmp];
							}
						}
						break;
					case ProtocolSettingsTypes.Windows:
						{
							let windows = <WindowsSettings>ps;
							this.allWindowsSettings = [...this.allWindowsSettings, windows];
						}
						break;
					case ProtocolSettingsTypes.Telnet:
						{
							let telnet = <TelnetSettings>ps;
							this.allTelnetSettings = [...this.allTelnetSettings, telnet];
						}
						break;
					case ProtocolSettingsTypes.Ssh:
						{
							let ssh = <SshSettings>ps;
							this.allSshSettings = [...this.allSshSettings, ssh];
						}
						break;
					case ProtocolSettingsTypes.HTTP:
						{
							let http = <HttpSettings>ps;
							this.allHttpSettings = [...this.allHttpSettings, http];
						}
						break;
					case ProtocolSettingsTypes.Vim:
						{
							let vim = <VimSettings>ps;
							this.allVimSettings = [...this.allVimSettings, vim];
						}
						break;
					case ProtocolSettingsTypes.Prtg:
						{
							let prtg = <PrtgSettings>ps;
							this.allPrtgSettings = [...this.allPrtgSettings, prtg];
						}
						break;
					default:
						throw new Error("Invalid protocol settings type: " + ps.SettingsType);
				}
			}, this);
		}
	}

    @ViewChild('snmpheader', { static: true })
	public snmpHeader: ElementRef;

    @ViewChild('snmpv3header', { static: true })
	public snmpV3Header: ElementRef;

    @ViewChild('windowsheader', { static: true })
	public windowsHeader: ElementRef;

    @ViewChild('telnetheader', { static: true })
	public telnetHeader: ElementRef;

    @ViewChild('sshheader', { static: true })
	public sshHeader: ElementRef;

	@ViewChild('httpheader', { static: true })
	public httpHeader: ElementRef;

    @ViewChild('vimheader', { static: true })
	public vimHeader: ElementRef;

    @ViewChild('prtgheader', { static: true })
	public prtgHeader: ElementRef;

    @ViewChild(Accordion, { static: true })
	public accordion: Accordion;

	public activePanelIndex: number = -1;
	public activePanel: SettingsPanel = SettingsPanel.None;

	private indexToPanelMap: SettingsPanel[] = [
		SettingsPanel.Snmp,
		SettingsPanel.SnmpV3,
		SettingsPanel.Windows,
		SettingsPanel.Telnet,
		SettingsPanel.Ssh,
		SettingsPanel.Http,
		SettingsPanel.Vim,
		SettingsPanel.Prtg
	];
	private indexToPanel(index: number): SettingsPanel {
		return this.indexToPanelMap[index];
	}
	private panelToIndex(panel: SettingsPanel): number {
		return this.indexToPanelMap.indexOf(panel);
	}

	private navType: NavType = NavType.None;
	private navCurrentEditor: Changeable = null;
	private navPanel: SettingsPanel = SettingsPanel.None;
	private navSettings: ProtocolSettings = null;
	public showTempChangesDialog: boolean = false;

	public showDeleteNotAllowedDialog: boolean = false;
	public protocolSettingsReferences: string[] = [];

	private resetNavState(): void {
		this.navType = NavType.None;
		this.navCurrentEditor = null;
		this.navPanel = SettingsPanel.None;
		this.navSettings = null;
		this.showTempChangesDialog = false;
	}

	private getCurrentEditor(): Changeable {
		if (this.children && this.children.length > 0) {
			return this.children.first;
		}
		return null;
	}

	private getSelectedSettings(): ProtocolSettings {
		if (this.selectedSnmpSettings)
			return this.selectedSnmpSettings;
		if (this.selectedSnmpV3Settings)
			return this.selectedSnmpV3Settings;
		if (this.selectedWindowsSettings)
			return this.selectedWindowsSettings;
		if (this.selectedTelnetSettings)
			return this.selectedTelnetSettings;
		if (this.selectedSshSettings)
			return this.selectedSshSettings;
		if (this.selectedHttpSettings)
			return this.selectedHttpSettings;
		if (this.selectedVimSettings)
			return this.selectedVimSettings;
		if (this.selectedPrtgSettings)
			return this.selectedPrtgSettings;
		return null;
	}

	private onSettingsSelection(navPanel: SettingsPanel, navSettings: ProtocolSettings): void {
		let child = this.getCurrentEditor();
		if (child) {
			if (child.tempChanges) {
				this.navType = NavType.SelectSettings;
				this.navCurrentEditor = child;
				this.navPanel = navPanel;
				this.navSettings = navSettings;
				this.showTempChangesDialog = true;
				return;
			}
		}

		this.selectSettings(navPanel, navSettings);
	}

	private onAddSettings(navPanel: SettingsPanel): void {
		let child = this.getCurrentEditor();
		if (child) {
			if (child.tempChanges) {
				this.navType = NavType.AddSettings;
				this.navCurrentEditor = child;
				this.navPanel = navPanel;
				this.navSettings = null;
				this.showTempChangesDialog = true;
				return;
			}
		}

		this.addSettings(navPanel);
	}

	private onDeleteSettings(navPanel: SettingsPanel, navSettings: ProtocolSettings): void {
		// If the currently-selected settings are still being used by
		// one or more discoveries, they cannot be deleted
		let psRefs: string[] = this.netFacadeService.findProtocolSettingsReferences(navSettings.GUID);
		if (psRefs.length > 0) {
			this.protocolSettingsReferences = psRefs;
			this.showDeleteNotAllowedDialog = true;
			return;
		}

		// If the currently-selected settings are being deleted,
		// there's no need to check for temp changes
		if (navSettings != this.getSelectedSettings()) {
			let child = this.getCurrentEditor();
			if (child) {
				if (child.tempChanges) {
					this.navType = NavType.DeleteSettings;
					this.navCurrentEditor = child;
					this.navPanel = navPanel;
					this.navSettings = navSettings;
					this.showTempChangesDialog = true;
					return;
				}
			}
		}

		this.deleteSettings(navPanel, navSettings);
	}

	public onCancelDeleteSettings(): void {
		this.showDeleteNotAllowedDialog = false;
		this.protocolSettingsReferences = [];
	}

	public onClosePanel(event): void {
		this.onChangePanel(NavType.ClosePanel, SettingsPanel.None);
	}

	public onOpenPanel(event): void {
		this.onChangePanel(NavType.OpenPanel, this.indexToPanel(event.index));
	}

	private onChangePanel(navType: NavType, navPanel: SettingsPanel): void {
		let child = this.getCurrentEditor();
		if (child) {
			if (child.tempChanges) {
				// Open temp changes dialog
				this.navType = navType;
				this.navCurrentEditor = child;
				this.navPanel = navPanel;
				this.navSettings = null;
				this.showTempChangesDialog = true;

				return;
			}
		}

		this.changePanel(navPanel);
	}

	public onDiscardTempChanges(): void {
		this.navCurrentEditor.discardTempChanges();
		this.completeNavigation();
		this.resetNavState();
	}

	public get applyTempChangesEnabled(): boolean {
		return (this.navCurrentEditor && this.navCurrentEditor.tempChangesValid);
	}

	private onApplyTempChanges(): void {
		this.navCurrentEditor.applyTempChanges();
		this.completeNavigation();
		this.resetNavState();
	}

	public onCancelNavigation(): void {
		this.resetNavState();
	}

	private completeNavigation(): void {
		switch (this.navType) {
			case NavType.SelectSettings:
				this.selectSettings(this.navPanel, this.navSettings);
				break;
			case NavType.AddSettings:
				this.addSettings(this.navPanel);
				break;
			case NavType.DeleteSettings:
				this.deleteSettings(this.navPanel, this.navSettings);
				break;
			case NavType.ClosePanel:
				// Close the current panel (so no panel is open)
				this.changePanel(SettingsPanel.None);
				break;
			case NavType.OpenPanel:
				// Open new panel
				this.changePanel(this.navPanel);
				break;
			default:
				throw new Error("Invalid navType");
		}
	}

	private changePanel(panel: SettingsPanel): void {
		this.activePanel = panel;
		this.activePanelIndex = this.panelToIndex(panel);
		this.resetSelectedSettings();
	}

	private addSettings(panel: SettingsPanel): void {

		this.onAppliedChange();

		switch (panel) {
			case SettingsPanel.Snmp:
				this.addSnmpSettings();
				break;
			case SettingsPanel.SnmpV3:
				this.addSnmpV3Settings();
				break;
			case SettingsPanel.Windows:
				this.addWindowsSettings();
				break;
			case SettingsPanel.Telnet:
				this.addTelnetSettings();
				break;
			case SettingsPanel.Ssh:
				this.addSshSettings();
				break;
			case SettingsPanel.Http:
				this.addHttpSettings();
				break;
			case SettingsPanel.Vim:
				this.addVimSettings();
				break;
			case SettingsPanel.Prtg:
				this.addPrtgSettings();
				break;
			default:
				throw new Error("Invalid panel");
		}
	}

	private deleteSettings(panel: SettingsPanel, settings: ProtocolSettings): void {

		this.onAppliedChange();

		switch (panel) {
			case SettingsPanel.Snmp:
				this.deleteSnmpSettings(<SnmpSettings>settings);
				break;
			case SettingsPanel.SnmpV3:
				this.deleteSnmpV3Settings(<SnmpSettings>settings);
				break;
			case SettingsPanel.Windows:
				this.deleteWindowsSettings(<WindowsSettings>settings);
				break;
			case SettingsPanel.Telnet:
				this.deleteTelnetSettings(<TelnetSettings>settings);
				break;
			case SettingsPanel.Ssh:
				this.deleteSshSettings(<SshSettings>settings);
				break;
			case SettingsPanel.Http:
				this.deleteHttpSettings(<HttpSettings>settings);
				break;
			case SettingsPanel.Vim:
				this.deleteVimSettings(<VimSettings>settings);
				break;
			case SettingsPanel.Prtg:
				this.deletePrtgSettings(<PrtgSettings>settings);
				break;
			default:
				throw new Error("Invalid panel");
		}
	}

	private selectSettings(panel: SettingsPanel, settings: ProtocolSettings): void {
		// There's no need to open/close panels here, because settings can only
		// be selected in the currently-open panel (i.e., settings selection does
		// not change the panel)

		this.resetSelectedSettings();
		this.allowShowChars = false;
		switch (panel) {
			case SettingsPanel.Snmp:
				this.selectedSnmpSettings = <SnmpSettings>settings;
				break;
			case SettingsPanel.SnmpV3:
				this.selectedSnmpV3Settings = <SnmpSettings>settings;
				break;
			case SettingsPanel.Windows:
				this.selectedWindowsSettings = <WindowsSettings>settings;
				break;
			case SettingsPanel.Telnet:
				this.selectedTelnetSettings = <TelnetSettings>settings;
				break;
			case SettingsPanel.Ssh:
				this.selectedSshSettings = <SshSettings>settings;
				break;
			case SettingsPanel.Http:
				this.selectedHttpSettings = <HttpSettings>settings;
				break;
			case SettingsPanel.Vim:
				this.selectedVimSettings = <VimSettings>settings;
				break;
			case SettingsPanel.Prtg:
				this.selectedPrtgSettings = <PrtgSettings>settings;
				break;
			default:
				throw new Error("Invalid panel");
		}
	}

	public onSelectSnmpSettings(settings: SnmpSettings): void {
		this.onSettingsSelection(SettingsPanel.Snmp, settings);
	}

	public onSelectSnmpV3Settings(settings: SnmpSettings): void {
		this.onSettingsSelection(SettingsPanel.SnmpV3, settings);
	}

	public onSelectWindowsSettings(settings: WindowsSettings): void {
		this.onSettingsSelection(SettingsPanel.Windows, settings);
	}

	public onSelectTelnetSettings(settings: TelnetSettings): void {
		this.onSettingsSelection(SettingsPanel.Telnet, settings);
	}

	public onSelectSshSettings(settings: SshSettings): void {
		this.onSettingsSelection(SettingsPanel.Ssh, settings);
	}

	public onSelectHttpSettings(settings: HttpSettings): void {
		this.onSettingsSelection(SettingsPanel.Http, settings);
	}

	public onSelectVimSettings(settings: VimSettings): void {
		this.onSettingsSelection(SettingsPanel.Vim, settings);
	}

	public onSelectPrtgSettings(settings: PrtgSettings): void {
		this.onSettingsSelection(SettingsPanel.Prtg, settings);
	}

	private resetSelectedSettings(): void {
		this.selectedSnmpSettings = null;
		this.selectedSnmpV3Settings = null;
		this.selectedWindowsSettings = null;
		this.selectedTelnetSettings = null;
		this.selectedSshSettings = null;
		this.selectedHttpSettings = null;
		this.selectedVimSettings = null;
		this.selectedPrtgSettings = null;
	}

	public onAddSnmpSettings() {
		this.onAddSettings(SettingsPanel.Snmp);
	}

	public onDeleteSnmpSettings(settings: SnmpSettings) {
		this.onDeleteSettings(SettingsPanel.Snmp, settings);
	}

	private addSnmpSettings(): void {
		if (this.hasTarget) {
			let newSettings = new SnmpSettings();
			newSettings.VerifyGuid();
			newSettings.Version = SnmpVersion.Ver1;
			newSettings.Name = "New SNMP";
			ProtocolSettingsUtils.ObfuscatePasswords(newSettings);
			this.allSnmpSettings = [...this.allSnmpSettings, newSettings];
			this.resetSelectedSettings();
			this.selectedSnmpSettings = newSettings;
			this.allowShowChars = true;

			this.target.ProtocolSettings.Add(newSettings);
		}
	}

	private deleteSnmpSettings(settings: SnmpSettings): void {
		let index = this.allSnmpSettings.indexOf(settings);
		if (index >= 0) {
			this.allSnmpSettings.splice(index, 1);
			if (this.selectedSnmpSettings === settings)
				this.selectedSnmpSettings = null;

			this.target.ProtocolSettings.Remove(settings);
		}
	}

	public onAddSnmpV3Settings() {
		this.onAddSettings(SettingsPanel.SnmpV3);
	}

	public onDeleteSnmpV3Settings(settings: SnmpSettings) {
		this.onDeleteSettings(SettingsPanel.SnmpV3, settings);
	}

	public addSnmpV3Settings(): void {
		if (this.hasTarget) {
			let newSettings = new SnmpSettings();
			newSettings.VerifyGuid();
			newSettings.Version = SnmpVersion.Ver3;
			newSettings.Name = "New SNMPV3";
			ProtocolSettingsUtils.ObfuscatePasswords(newSettings);
			this.allSnmpV3Settings.push(newSettings);
			this.resetSelectedSettings();
			this.selectedSnmpV3Settings = newSettings;
			this.allowShowChars = true;

			this.target.ProtocolSettings.Add(newSettings);
		}
	}

	public deleteSnmpV3Settings(settings: SnmpSettings): void {
		let index = this.allSnmpV3Settings.indexOf(settings);
		if (index >= 0) {
			this.allSnmpV3Settings.splice(index, 1);
			if (this.selectedSnmpV3Settings === settings)
				this.selectedSnmpV3Settings = null;

			this.target.ProtocolSettings.Remove(settings);
		}
	}

	public onAddWindowsSettings() {
		this.onAddSettings(SettingsPanel.Windows);
	}

	public onDeleteWindowsSettings(settings: WindowsSettings) {
		this.onDeleteSettings(SettingsPanel.Windows, settings);
	}

	public addWindowsSettings(): void {
		if (this.hasTarget) {
			let newSettings = new WindowsSettings();
			newSettings.VerifyGuid();
			newSettings.Name = "New Windows";
			ProtocolSettingsUtils.ObfuscatePasswords(newSettings);
			this.allWindowsSettings.push(newSettings);
			this.resetSelectedSettings();
			this.selectedWindowsSettings = newSettings;
			this.allowShowChars = true;

			this.target.ProtocolSettings.Add(newSettings);
		}
	}

	public deleteWindowsSettings(settings: WindowsSettings): void {
		let index = this.allWindowsSettings.indexOf(settings);
		if (index >= 0) {
			this.allWindowsSettings.splice(index, 1);
			if (this.selectedWindowsSettings === settings)
				this.selectedWindowsSettings = null;

			this.target.ProtocolSettings.Remove(settings);
		}
	}

	public onAddVimSettings() {
		this.onAddSettings(SettingsPanel.Vim);
	}

	public onDeleteVimSettings(settings: VimSettings) {
		this.onDeleteSettings(SettingsPanel.Vim, settings);
	}

	public addVimSettings(): void {
		if (this.hasTarget) {
			let newSettings = new VimSettings();
			newSettings.VerifyGuid();
			newSettings.Name = "New VMware";
			ProtocolSettingsUtils.ObfuscatePasswords(newSettings);
			this.allVimSettings.push(newSettings);
			this.resetSelectedSettings();
			this.selectedVimSettings = newSettings;
			this.allowShowChars = true;

			this.target.ProtocolSettings.Add(newSettings);
		}
	}

	public deleteVimSettings(settings: VimSettings): void {
		let index = this.allVimSettings.indexOf(settings);
		if (index >= 0) {
			this.allVimSettings.splice(index, 1);
			if (this.selectedVimSettings === settings)
				this.selectedVimSettings = null;

			this.target.ProtocolSettings.Remove(settings);
		}
	}

	public onAddSshSettings() {
		this.onAddSettings(SettingsPanel.Ssh);
	}

	public onDeleteSshSettings(settings: SshSettings) {
		this.onDeleteSettings(SettingsPanel.Ssh, settings);
	}

	public addSshSettings(): void {
		if (this.hasTarget) {
			let newSettings = new SshSettings();
			newSettings.VerifyGuid();
			newSettings.Name = "New SSH";
			ProtocolSettingsUtils.ObfuscatePasswords(newSettings);
			this.allSshSettings.push(newSettings);
			this.resetSelectedSettings();
			this.selectedSshSettings = newSettings;
			this.allowShowChars = true;

			this.target.ProtocolSettings.Add(newSettings);
		}
	}

	public deleteSshSettings(settings: SshSettings): void {
		let index = this.allSshSettings.indexOf(settings);
		if (index >= 0) {
			this.allSshSettings.splice(index, 1);
			if (this.selectedSshSettings === settings)
				this.selectedSshSettings = null;

			this.target.ProtocolSettings.Remove(settings);
		}
	}

	public onAddHttpSettings() {
		this.onAddSettings(SettingsPanel.Http);
	}

	public onDeleteHttpSettings(settings: HttpSettings) {
		this.onDeleteSettings(SettingsPanel.Http, settings);
	}

	public addHttpSettings(): void {
		if (this.hasTarget) {
			let newSettings = new HttpSettings();
			newSettings.VerifyGuid();
			newSettings.Name = "New HTTP";
			ProtocolSettingsUtils.ObfuscatePasswords(newSettings);
			this.allHttpSettings.push(newSettings);
			this.resetSelectedSettings();
			this.selectedHttpSettings = newSettings;
			this.allowShowChars = true;

			this.target.ProtocolSettings.Add(newSettings);
		}
	}

	public deleteHttpSettings(settings: HttpSettings): void {
		let index = this.allHttpSettings.indexOf(settings);
		if (index >= 0) {
			this.allHttpSettings.splice(index, 1);
			if (this.selectedHttpSettings === settings)
				this.selectedHttpSettings = null;

			this.target.ProtocolSettings.Remove(settings);
		}
	}

	public onAddTelnetSettings() {
		this.onAddSettings(SettingsPanel.Telnet);
	}

	public onDeleteTelnetSettings(settings: TelnetSettings) {
		this.onDeleteSettings(SettingsPanel.Telnet, settings);
	}

	public addTelnetSettings(): void {
		if (this.hasTarget) {
			let newSettings = new TelnetSettings();
			newSettings.VerifyGuid();
			newSettings.Name = "New Telnet";
			ProtocolSettingsUtils.ObfuscatePasswords(newSettings);
			this.allTelnetSettings.push(newSettings);
			this.resetSelectedSettings();
			this.selectedTelnetSettings = newSettings;
			this.allowShowChars = true;

			this.target.ProtocolSettings.Add(newSettings);
		}
	}

	public deleteTelnetSettings(settings: TelnetSettings): void {
		let index = this.allTelnetSettings.indexOf(settings);
		if (index >= 0) {
			this.allTelnetSettings.splice(index, 1);
			if (this.selectedTelnetSettings === settings)
				this.selectedTelnetSettings = null;

			this.target.ProtocolSettings.Remove(settings);
		}
	}

	public onAddPrtgSettings() {
		this.onAddSettings(SettingsPanel.Prtg);
	}

	public onDeletePrtgSettings(settings: PrtgSettings) {
		this.onDeleteSettings(SettingsPanel.Prtg, settings);
	}

	public addPrtgSettings(): void {
		if (this.hasTarget) {
			let newSettings = new PrtgSettings();
			newSettings.VerifyGuid();
			newSettings.Name = "New PRTG";
			ProtocolSettingsUtils.ObfuscatePasswords(newSettings);
			this.allPrtgSettings.push(newSettings);
			this.resetSelectedSettings();
			this.selectedPrtgSettings = newSettings;
			this.allowShowChars = true;

			this.target.ProtocolSettings.Add(newSettings);
		}
	}

	public deletePrtgSettings(settings: PrtgSettings): void {
		let index = this.allPrtgSettings.indexOf(settings);
		if (index >= 0) {
			this.allPrtgSettings.splice(index, 1);
			if (this.selectedPrtgSettings === settings)
				this.selectedPrtgSettings = null;

			this.target.ProtocolSettings.Remove(settings);
		}
	}

	//
	// Changeable
	//

	public get tempChanges(): boolean {
		let child = this.getCurrentEditor();
		if (child) {
			return child.tempChanges;
		}
		else {
			return false;
		}
	}

	// PRE: tempChanges === true
	public get tempChangesValid(): boolean {
		let child = this.getCurrentEditor();
		if (child && child.tempChanges) {
			return child.tempChangesValid;
		}
		else {
			console.assert(false, "tempChangesValid called in invalid state");
			return false;
		}
	}

	// PRE: tempChanges === true && tempChangesValid === false
	public get invalidTempChanges(): string[] {
		let child = this.getCurrentEditor();
		if (child && child.tempChanges && !child.tempChangesValid) {
			let result = child.invalidTempChanges;
			return result.map((value: string) => "Protocols/Credentials: " + value, this);
		}
		else {
			console.assert(false, "invalidTempChanges called in invalid state");
			return [];
		}
	}

	// PRE: tempChanges === true && tempChangesValid === true
	public applyTempChanges(): void {
		let child = this.getCurrentEditor();
		if (child && child.tempChanges && child.tempChangesValid) {
			child.applyTempChanges();
		}
		else {
			console.assert(false, "applyTempChanges called in invalid state");
		}
	}

	// PRE: tempChanges === true
	public discardTempChanges(): void {
		let child = this.getCurrentEditor();
		if (child && child.tempChanges) {
			child.discardTempChanges();
			//this.changeDetector.markForCheck();
		}
		else {
			console.assert(false, "discardTempChanges called in invalid state");
		}
	}

}
